import type { ConfigRestore, ConfigSnapshotListItem } from '@/hooks/useConfigSnapshots';

/** One line of a device's config timeline: a snapshot, or a restore. */
export type ConfigTimelineItem =
  | { kind: 'snapshot'; snapshot: ConfigSnapshotListItem; index: number }
  | { kind: 'restore'; restore: ConfigRestore; target: ConfigSnapshotListItem | null };

/**
 * Snapshots and restores, newest first. A restore sits right above the
 * snapshot it replaced, which was captured just before it ran, so the timeline
 * reads: the config it replaced, the restore, then the config it put back. A
 * restore whose replaced snapshot isn't listed sits where it started. `index`
 * is the snapshot's place in `snapshots`.
 */
export function configTimeline(
  snapshots: ConfigSnapshotListItem[],
  restores: ConfigRestore[],
): ConfigTimelineItem[] {
  const byId = new Map(snapshots.map((s) => [s.id, s]));
  const at = (iso: string) => Date.parse(iso);
  const items: { at: number; item: ConfigTimelineItem }[] = [
    ...snapshots.map((snapshot, index) => ({
      at: at(snapshot.createdAt),
      item: { kind: 'snapshot' as const, snapshot, index },
    })),
    ...restores.map((restore) => {
      const replaced = restore.replacedSnapshotId ? byId.get(restore.replacedSnapshotId) : undefined;
      return {
        // Just after the snapshot it replaced.
        at: replaced ? at(replaced.createdAt) + 1 : at(restore.startedAt ?? restore.createdAt),
        item: {
          kind: 'restore' as const,
          restore,
          target: (restore.snapshotId ? byId.get(restore.snapshotId) : undefined) ?? null,
        },
      };
    }),
  ];
  return items.sort((a, b) => b.at - a.at).map((i) => i.item);
}
